import { useMemo } from 'react';
import debounce from 'lodash/debounce';
import { fetchQuery, graphql, useRelayEnvironment, type Environment } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { ANALYTICS_PACKAGE } from '../../common/constants.tsx';
import type { CascadingSelectOption } from '../../common/types.tsx';
import type {
	useCascadingSelectValuesLoaderUseCascadingSelectValuesLoaderQuery,
	useCascadingSelectValuesLoaderUseCascadingSelectValuesLoaderQuery$variables,
	JiraCascadingSelectOptionType,
} from './__generated__/useCascadingSelectValuesLoaderUseCascadingSelectValuesLoaderQuery.graphql';
import messages from './messages.tsx';

type Props = {
	jqlTerm: string;
	jqlContext: string;
	optionType: JiraCascadingSelectOptionType;
	parentOptionId?: string;
	defaultOption?: CascadingSelectOption;
};

export const fetchAllOptions = async (
	environment: Environment,
	variables: useCascadingSelectValuesLoaderUseCascadingSelectValuesLoaderQuery$variables,
): Promise<Array<CascadingSelectOption>> => {
	const result =
		await fetchQuery<useCascadingSelectValuesLoaderUseCascadingSelectValuesLoaderQuery>(
			environment,
			graphql`
				query useCascadingSelectValuesLoaderUseCascadingSelectValuesLoaderQuery(
					$cloudId: ID!
					$after: String
					$jqlTerm: String!
					$jqlContext: String
					$searchString: String
					$optionType: JiraCascadingSelectOptionType!
					$parentOptionId: ID
				) {
					jira @required(action: THROW) {
						jqlBuilder(cloudId: $cloudId) @required(action: THROW) {
							cascadingSelectValues(
								after: $after
								jqlTerm: $jqlTerm
								jqlContext: $jqlContext
								filter: { optionType: $optionType, parentOptionId: $parentOptionId }
								searchString: $searchString
							) @required(action: THROW) {
								pageInfo {
									hasNextPage
									endCursor
								}
								edges @required(action: THROW) {
									node @required(action: THROW) {
										displayName
										optionId @required(action: THROW)
									}
								}
							}
						}
					}
				}
			`,
			variables,
			{ fetchPolicy: 'store-or-network' },
		).toPromise();

	if (!result) {
		throw new Error(
			'Unexpected empty result received from useCascadingSelectValuesLoaderUseCascadingSelectValuesLoaderQuery',
		);
	}
	const { pageInfo, edges } = result.jira.jqlBuilder.cascadingSelectValues;

	const options: CascadingSelectOption[] = edges
		.map((edge): CascadingSelectOption | undefined =>
			edge
				? {
						label: edge.node.displayName,
						value: edge.node.optionId,
					}
				: undefined,
		)
		.filter(Boolean);

	return pageInfo.hasNextPage && pageInfo.endCursor
		? options.concat(
				await fetchAllOptions(environment, {
					...variables,
					after: pageInfo.endCursor,
				}),
			)
		: options;
};

export const useCascadingSelectValuesLoader = ({
	jqlTerm,
	jqlContext,
	optionType,
	parentOptionId = undefined,
}: Props) => {
	const environment = useRelayEnvironment();
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();

	return useMemo(
		() => ({
			loadOptions: debounce(
				// Must return void/undefined. AK select ignores the callback if a promise/async is returned.
				(searchString: string, cb: (options: Array<CascadingSelectOption>) => void): void => {
					fetchAllOptions(environment, {
						searchString,
						cloudId,
						jqlTerm,
						jqlContext,
						optionType,
						parentOptionId,
					})
						.catch((error) => {
							fireErrorAnalytics({
								error,
								sendToPrivacyUnsafeSplunk: true,
								meta: {
									id: 'useCascadingSelectValuesLoaderError',
									packageName: ANALYTICS_PACKAGE,
									teamName: 'empanada',
								},
							});

							return [
								{
									label: formatMessage(messages.fetchError),
									value: '',
									isDisabled: true,
								},
							];
						})
						.then(cb);
				},
				500,
			),
		}),
		[cloudId, environment, formatMessage, jqlContext, jqlTerm, optionType, parentOptionId],
	);
};
